/**
 * converters.js
 *
 * DashboardProgressManagementPageから抽出された純粋関数群
 * データ変換・ID解決・オプション生成などのロジックを提供
 */

import {
  DELIVERABLE_MESSAGES, NO_DELIVERABLE_ALIASES,
} from '../constants/messages.js';

/**
 * 成果物名の保存形をそろえる。バックエンドの normalize_product と同じ規則。
 *
 * NFKC 正規化で全角英数字を半角に・半角カナを全角に・全角スペースを半角にし、
 * 連続する空白は1つにまとめて前後を落とす。大文字小文字とひらがなカタカナは
 * 変換しない（表記の違いをそのまま残し、同一判定はDBの照合順序が区別する）。
 */
export function normalizeProductName(product) {
  return String(product ?? '').normalize('NFKC').split(/\s+/).filter(Boolean).join(' ');
}

/** 成果物名が「成果物なし」を表す表記かどうか。 */
export function isNoDeliverableLabel(product) {
  return NO_DELIVERABLE_ALIASES.includes(normalizeProductName(product));
}

/**
 * 列が必須かどうか。
 * required には真偽値のほか ({ row }) => boolean を渡せる。
 * 行の内容によって必須が変わる列（成果物「なし」のときだけURL不要など）で使う。
 */
export function isColumnRequired(column, row) {
  if (typeof column?.required === 'function') return Boolean(column.required({ row }));
  return Boolean(column?.required);
}

/**
 * 複数キーに対して、行データから一意な値を抽出してセレクトボックス用のオプションを生成
 * @param {Array} rows - データ行の配列
 * @param {Array} keys - 抽出対象のキー名配列
 * @returns {Object} キーごとのオプション配列 { key: ['', value1, value2, ...] }
 */
export function buildSelectOptions(rows, keys) {
  const optionsByKey = Object.fromEntries(keys.map((key) => [key, new Set()]));

  rows.forEach((row) => {
    keys.forEach((key) => {
      const value = row[key];
      if (value !== undefined && value !== null && value !== '') {
        optionsByKey[key].add(value);
      }
    });
  });

  return Object.fromEntries(
    Object.entries(optionsByKey).map(([key, values]) => [key, ['', ...Array.from(values)]]),
  );
}

/**
 * 大分類ごとに中分類の選択肢を生成
 * @param {Array} rows - データ行の配列
 * @returns {Object} { large_category_name: ['', middle1, middle2, ...] }
 */
export function buildMiddleCategoryOptionsByLarge(rows) {
  const map = new Map();

  rows.forEach((row) => {
    const largeCategory = row.large_category_name || '';
    const middleCategory = row.middle_category_name || '';

    if (!largeCategory) return;

    if (!map.has(largeCategory)) {
      map.set(largeCategory, new Set());
    }

    if (middleCategory) {
      map.get(largeCategory).add(middleCategory);
    }
  });

  return Object.fromEntries(
    Array.from(map.entries()).map(([largeCategory, middleCategories]) => [
      largeCategory,
      ['', ...Array.from(middleCategories)],
    ]),
  );
}

/**
 * 中分類ごとに小分類の選択肢を生成
 * @param {Array} smallCategoryData - 小分類データの配列
 * @returns {Object} { middle_category_name: ['', small1, small2, ...] }
 */
export function buildSmallCategoryOptionsByMiddle(smallCategoryData) {
  const map = new Map();

  smallCategoryData.forEach((item) => {
    const middleName = item.middle_category?.name || '';
    if (!middleName) return;

    if (!map.has(middleName)) {
      map.set(middleName, new Set());
    }
    map.get(middleName).add(item.small_category);
  });

  return Object.fromEntries(
    Array.from(map.entries()).map(([middleName, smallCategories]) => [
      middleName,
      ['', ...Array.from(smallCategories)],
    ]),
  );
}

/**
 * 小分類の名前からIDを引くためのルックアップMapを生成
 * @param {Array} items - 小分類データの配列
 * @returns {Object} { pairMap: Map<'middle::small', id>, byNameMap: Map<'small', id> }
 */
export function buildSmallCategoryLookup(items) {
  const pairMap = new Map();
  const byNameMap = new Map();

  items.forEach((item) => {
    const smallName = item.small_category;
    const middleName = item.middle_category?.name;
    if (smallName) {
      if (!byNameMap.has(smallName)) {
        byNameMap.set(smallName, item.id);
      }
      if (middleName) {
        pairMap.set(`${middleName}::${smallName}`, item.id);
      }
    }
  });

  return { pairMap, byNameMap };
}

/**
 * 中項目IDごとに小分類の選択肢を生成。
 * 同名中項目が複数の大項目に属するケースがあるため、名前でなく中項目IDでキーする
 * （選択中の中項目に厳密に所属する小項目だけを候補にするため）。
 * @param {Array} smallCategoryData - 小分類データの配列（middle_category.id を含む）
 * @returns {Object} { middleId: ['', small1, small2, ...] }
 */
export function buildSmallCategoryOptionsByMiddleId(smallCategoryData) {
  const map = new Map();
  smallCategoryData.forEach((item) => {
    const middleId = item.middle_category?.id;
    if (middleId === undefined || middleId === null) return;
    if (!map.has(middleId)) map.set(middleId, new Set());
    if (item.small_category) map.get(middleId).add(item.small_category);
  });
  return Object.fromEntries(
    Array.from(map.entries()).map(([middleId, smalls]) => [middleId, ['', ...Array.from(smalls)]]),
  );
}

/**
 * (中項目ID::小項目名) から 小項目ID を引くルックアップMapを生成。
 * 同名中項目の衝突を避けるため中項目IDでスコープする。
 * @param {Array} items - 小分類データの配列（middle_category.id を含む）
 * @returns {Map<'middleId::small', id>}
 */
export function buildSmallCategoryLookupByMiddleId(items) {
  const map = new Map();
  items.forEach((item) => {
    const middleId = item.middle_category?.id;
    if (middleId !== undefined && middleId !== null && item.small_category && item.id) {
      map.set(`${middleId}::${item.small_category}`, item.id);
    }
  });
  return map;
}

/**
 * 行データを正規化（milestone をブール値に変換）
 * @param {Array} rows - データ行の配列
 * @returns {Array} 正規化された行の配列
 */
export function normalizeRows(rows) {
  return rows.map((row) => ({
    ...row,
    milestone: Boolean(row.milestone),
  }));
}

/**
 * OEM/リージョンごとに後続タスクの選択肢を生成
 * @param {Array} rows - データ行の配列
 * @returns {Object} { oem_region: ['', task1, task2, ...] }
 */
export function buildSuccessorTaskOptionsByOemRegion(rows) {
  const map = {};

  rows.forEach((row) => {
    const key = row.oem_region || '';
    if (!map[key]) map[key] = new Set();
    if (row.small_category) {
      map[key].add(row.small_category);
    }
    if (row.successor_task) {
      String(row.successor_task).split(',').map((s) => s.trim()).filter(Boolean)
        .forEach((s) => map[key].add(s));
    }
  });

  const result = {};
  Object.keys(map).forEach((key) => {
    result[key] = ['', ...Array.from(map[key])];
  });
  return result;
}

/**
 * マスタレコードから選択肢を生成（複数のpicker関数で値を抽出）
 * @param {Array} items - マスタデータの配列
 * @param {Array} pickers - 値を抽出する関数の配列
 * @returns {Array} ['', value1, value2, ...]
 */
/**
 * OEM×対地ごとに選択肢を生成
 * @param {Array} items - レコード配列（oemRegion または oem_region を持つ）
 * @param {Array} pickers - 表示名を抽出する関数の配列（最初に値が取れたものを採用）
 * @returns {Object} { 'OEM×対地': ['', label1, label2, ...] }
 * @note 関連リスク・関連課題などの候補を行のOEM×対地に限定し、
 *       別OEM×対地レコードへの誤リンクを入力段階で防ぐ
 */
export function buildOptionsByOemRegion(items, pickers = []) {
  const grouped = {};
  (items || []).forEach((item) => {
    const label = pickers
      .map((picker) => picker(item))
      .find((v) => v !== undefined && v !== null && v !== '');
    if (label === undefined || label === null || label === '') return;
    const key = item.oemRegion || item.oem_region || '';
    if (!key) return;
    if (!grouped[key]) grouped[key] = [''];
    if (!grouped[key].includes(label)) grouped[key].push(label);
  });
  return grouped;
}

export function buildMasterRecordOptions(items, pickers = []) {
  const values = new Set();

  // マスタ取得が空応答（null）でも候補ゼロとして扱う。呼び出し元は取得失敗時も
  // 画面を描き続けるため、ここで落とすと一覧全体が出なくなる。
  // 同ファイルの buildOptionsByOemRegion と同じ守り方に揃える。
  (items || []).forEach((item) => {
    const value = pickers
      .map((picker) => picker(item))
      .find((v) => v !== undefined && v !== null && v !== '');
    if (value !== undefined && value !== null && value !== '') {
      values.add(value);
    }
  });

  return ['', ...Array.from(values)];
}

/**
 * マスタデータから名前→IDのルックアップMapを生成
 * @param {Array} items - マスタデータの配列
 * @param {string} key - キー名（名前フィールド）
 * @param {string} idKey - ID フィールド名（デフォルト: 'id'）
 * @returns {Map} Map<name, id>
 */
export function buildLookupMap(items, key, idKey = 'id') {
  const map = new Map();
  items.forEach((item) => {
    if (item[key] !== undefined && item[key] !== null && item[key] !== '') {
      map.set(item[key], item[idKey]);
    }
  });
  return map;
}

/**
 * カンマ区切りの従業員表示名からIDの配列を解決
 * @param {string} displayValue - カンマ区切りの表示名
 * @param {Map} employeeByName - 名前→IDのMap
 * @returns {Array} IDの配列
 */
export function resolveEmployeeIds(displayValue, employeeByName) {
  if (!displayValue) return [];
  return displayValue
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => employeeByName.get(name))
    .filter(Boolean);
}

/**
 * 中分類の名前からIDを引くためのルックアップMapを生成
 * @param {Array} items - 中分類データの配列
 * @returns {Object} { pairMap: Map<'large::middle', id>, byNameMap: Map<'middle', id> }
 */
export function buildMiddleCategoryLookup(items) {
  const pairMap = new Map();
  const byNameMap = new Map();

  items.forEach((item) => {
    const middleName = item.middle_category;
    const largeName = item.large_category?.name;
    if (middleName) {
      if (!byNameMap.has(middleName)) {
        byNameMap.set(middleName, item.id);
      }
      if (largeName) {
        pairMap.set(`${largeName}::${middleName}`, item.id);
      }
    }
  });

  return { pairMap, byNameMap };
}

/**
 * 後続タスクの表示名からIDの配列を解決
 * @param {Object} row - データ行
 * @param {Map} scheduleByOemSmall - 'oem_region::small_category' → IDのMap
 * @returns {Array} IDの配列
 */
export function resolveSuccessorTaskIds(row, scheduleByOemSmall) {
  if (!row.successor_task) return [];
  const names = String(row.successor_task).split(',').map((s) => s.trim()).filter(Boolean);
  const oemRegion = row.oem_region || '';
  const ids = [];
  names.forEach((name) => {
    const id = scheduleByOemSmall.get(`${oemRegion}::${name}`);
    if (id) ids.push(id);
  });
  return ids;
}

/**
 * カンマ区切りの表示値から名前→IDのMapを使ってIDの配列を解決
 * @param {string} displayValue - カンマ区切りの表示値
 * @param {Map} lookupMap - 名前→IDのMap
 * @returns {Array} IDの配列
 */
export function resolveIdsByName(displayValue, lookupMap) {
  if (!displayValue) return [];
  return String(displayValue).split(',').map((s) => s.trim()).filter(Boolean)
    .map((name) => lookupMap.get(name))
    .filter(Boolean);
}

/**
 * 日付値をnullに変換（空文字列など falsy な値を null にする）
 * @param {string|null|undefined} value - 日付値
 * @returns {string|null}
 */
export function toNullableDate(value) {
  return value || null;
}

/**
 * 成果物セットの相互必須バリデーション。
 * 各成果物について、成果物名があればURL必須・URLがあれば成果物名必須。
 * 両方空の行はスキップ扱い（バックエンドで無視される）。
 * @param {Array} deliverables - [{ product, deliverable_url, ... }]
 * @returns {string|null} エラーメッセージ（問題なければ null）
 */
export function validateDeliverables(deliverables) {
  const list = Array.isArray(deliverables) ? deliverables : [];
  const filled = list.filter(
    (d) => d.id || String(d.product || '').trim() || String(d.deliverable_url || '').trim(),
  );
  // 1つの進捗管理（＝1つの領域）の中では「成果物なし」と通常の成果物は併存できない
  if (filled.length > 1 && filled.some((d) => d.no_deliverable)) {
    return DELIVERABLE_MESSAGES.NO_DELIVERABLE_CONFLICT;
  }
  for (const d of list) {
    // 既存成果物の選択（id あり）は成果物管理側の責任のため検証対象外
    if (d.id) continue;
    const name = normalizeProductName(d.product);
    const hasName = Boolean(name);
    const hasUrl = Boolean(String(d.deliverable_url || '').trim());
    // 「成果物なし」は URL が任意項目
    if (d.no_deliverable) continue;
    // 「なし」はフラグが持つ予約語。テキストで登録できると、見た目が同じで
    // フラグだけ違うレコードができ、併存禁止も重複判定もすり抜ける。
    if (isNoDeliverableLabel(name)) {
      return DELIVERABLE_MESSAGES.RESERVED_PRODUCT_NAME(name);
    }
    if (hasName && !hasUrl) return DELIVERABLE_MESSAGES.DELIVERABLE_URL_REQUIRED;
    if (hasUrl && !hasName) return DELIVERABLE_MESSAGES.DELIVERABLE_PRODUCT_REQUIRED;
  }
  return null;
}

/**
 * レコードデータをAPIペイロード形式に変換
 * @param {Object} row - 画面表示用レコード
 * @param {Object} refs - マスタデータのルックアップMap
 * @param {Object} defaults - デフォルト値（oemId, regionId）
 * @returns {Object|null} APIペイロード（必須項目不足時はnull）
 */
export function toPayload(row, refs, defaults) {
  let oemId;
  let regionId;
  if (row.oem_region && refs.oemRegionLookup.has(row.oem_region)) {
    const resolved = refs.oemRegionLookup.get(row.oem_region);
    oemId = resolved.oemId;
    regionId = resolved.regionId;
  } else {
    oemId = refs.oemByName.get(row.oem_name) || defaults.oemId;
    regionId = refs.regionByName.get(row.region_name) || defaults.regionId;
  }
  const categoryId = refs.categoryByName.get(row.category_name);
  const actorId = refs.actorByName.get(row.actor_name);
  const statusId = refs.statusByName.get(row.status_name);
  const middleKey = `${row.large_category_name || ''}::${row.middle_category_name || ''}`;
  const middleCategoryId = row.middle_category_name
    ? (refs.middleByPair.get(middleKey) || refs.middleByName.get(row.middle_category_name) || null)
    : null;
  const smallKey = `${row.middle_category_name || ''}::${row.small_category || ''}`;
  const smallCategoryId = row.small_category
    ? (refs.smallByPair.get(smallKey) || refs.smallByName.get(row.small_category) || null)
    : null;

  const phaseId = row.phase_name
    ? (refs.phaseByName.get(row.phase_name) || null)
    : null;

  if (!oemId || !regionId || !categoryId || !actorId || !middleCategoryId || !smallCategoryId) {
    return null;
  }

  return {
    oem_id: oemId,
    region_id: regionId,
    phase_id: phaseId,
    category_id: categoryId,
    middle_category_id: middleCategoryId,
    small_category_id: smallCategoryId,
    milestone: Boolean(row.milestone),
    // 成果物は成果物1件ごとの1セット（名前・URL・保管済み・PMO成果物保管チェック）
    deliverables: (Array.isArray(row.deliverables) ? row.deliverables : []).map((d) => ({
      id: d.id ?? null,
      product: String(d.product || '').trim(),
      deliverable_url: d.deliverable_url || '',
      no_deliverable: Boolean(d.no_deliverable),
      is_deliverable_stored: Boolean(d.is_deliverable_stored),
      pmo_deliverable_check: Boolean(d.pmo_deliverable_check),
    })),
    // 成果物リンクの競合検出用（中間テーブルのバージョン）
    link_version: row.link_version ?? null,
    actor_id: actorId,
    status_id: statusId || null,
    start_date_plan: toNullableDate(row.start_date_plan),
    end_date_plan: toNullableDate(row.end_date_plan),
    start_date_achievement: toNullableDate(row.start_date_achievement),
    end_date_achievement: toNullableDate(row.end_date_achievement),
    memo: row.memo || '',
    employee_ids: resolveEmployeeIds(row.assignees_display, refs.employeeByName),
    successor_task_ids: resolveSuccessorTaskIds(row, refs.scheduleByOemSmall),
    risk_ids: resolveIdsByName(row.related_risk, refs.riskByTitle),
    problem_ids: resolveIdsByName(row.related_problem, refs.problemByTitle),
    updated_at_iso: row.updated_at_iso ?? null,
  };
}

// 進捗管理1件の座標（OEM×対地＋カテゴリ〜Phase）を1つのキーにまとめる。
// 成果物候補をこの単位で絞り込む。
export function scheduleCoordinateKey(schedule) {
  return [
    schedule?.oem_region || '',
    schedule?.category_name || '',
    schedule?.large_category_name || '',
    schedule?.middle_category_name || '',
    schedule?.small_category || '',
    schedule?.phase_name || '',
  ].join(' / ');
}

// 成果物候補を座標単位に振り分ける。
// どの進捗管理にも紐付いていない成果物は座標が決まらないので別枠で持つ。
export function buildDeliverableOptionsByCoordinate(deliverables, schedules) {
  const coordinateByScheduleId = new Map(
    (schedules || []).map((schedule) => [schedule.id, scheduleCoordinateKey(schedule)]),
  );

  const byCoordinate = {};
  const unlinked = [];
  (deliverables || []).forEach((deliverable) => {
    const option = {
      id: deliverable.id,
      product: deliverable.product || '',
      deliverable_url: deliverable.deliverableUrl || '',
      no_deliverable: Boolean(deliverable.noDeliverable),
    };
    const coordinateKeys = new Set(
      (deliverable.progress_schedule_ids || [])
        .map((scheduleId) => coordinateByScheduleId.get(scheduleId))
        .filter(Boolean),
    );
    if (coordinateKeys.size === 0) {
      unlinked.push(option);
      return;
    }
    coordinateKeys.forEach((key) => {
      if (!byCoordinate[key]) byCoordinate[key] = [];
      byCoordinate[key].push(option);
    });
  });

  return { byCoordinate, unlinked };
}

// 進捗管理1行で選べる成果物候補。
// 同じ座標に登録済みのものと、まだどこにも紐付いていないものだけを返す
// （別の小項目で登録済みの成果物は候補に出さない）。
export function getDeliverableOptionsForSchedule(options, row) {
  const sameCoordinate = options?.byCoordinate?.[scheduleCoordinateKey(row)] || [];
  const seen = new Set(sameCoordinate.map((option) => option.id));
  // 座標が決まっていない成果物も選べるようにするが、この行の座標のものと
  // 区別できないと「なし」の再利用先を誤るため、印を付けて返す。
  const unlinked = (options?.unlinked || [])
    .filter((option) => !seen.has(option.id))
    .map((option) => ({ ...option, unlinked: true }));
  return [...sameCoordinate, ...unlinked];
}
